// Shared status/action tag colors used across the back office.
// Dashboard, Actividad, ContentMap and EntityHistory all consume these — the
// audit tags used to be duplicated per view, which meant a fix had to be
// applied three times to actually stick.

export interface StatusTagStyle {
  // antd Tag preset color name.
  color: string;
  // Spanish UI label.
  label: string;
}

// Audit log action → tag color/label. Keyed by plain string, not by a closed
// union: the backend's `action` is free-form, so this map is a set of KNOWN
// verbs rather than an exhaustive one. Everything reads it through
// resolveAuditActionTag.
export const AUDIT_ACTION_TAGS: Record<string, StatusTagStyle> = {
  // Derived from the HTTP method by audit-log.interceptor.ts.
  CREATE: { color: "green", label: "Creó" },
  UPDATE: { color: "blue", label: "Editó" },
  DELETE: { color: "red", label: "Eliminó" },
  // Logged explicitly by school-users.service.ts / school-users-bulk.service.ts.
  READ_CREDENTIALS: { color: "purple", label: "Vio credenciales" },
  REGENERATE_CREDENTIALS: { color: "orange", label: "Regeneró credenciales" },
  BULK_COMMIT: { color: "geekblue", label: "Carga masiva" },
};

const UNKNOWN_ACTION_COLOR = "default";

/**
 * Every action the back office knows how to name, in display order. Derived
 * from AUDIT_ACTION_TAGS rather than hand-listed, so the Actividad filter can
 * never offer a verb the tag map cannot render — or miss one it can.
 */
export const KNOWN_AUDIT_ACTIONS: string[] = Object.keys(AUDIT_ACTION_TAGS);

/**
 * Total lookup for an audit action's tag.
 *
 * Indexing AUDIT_ACTION_TAGS directly is what broke the Dashboard, the
 * Actividad table and EntityHistory: any verb outside the CRUD three yielded
 * `undefined`, and reading `.color` off it threw inside a render, taking the
 * whole card down. A new backend verb must degrade to a readable neutral tag
 * instead — so this never returns undefined, for any input.
 *
 * `hasOwnProperty` rather than a bare lookup: "constructor" and "toString"
 * would otherwise resolve to inherited Object.prototype members, which are
 * truthy but carry no color — the same crash by another route.
 */
export const resolveAuditActionTag = (
  action: string | null | undefined
): StatusTagStyle => {
  if (action && Object.prototype.hasOwnProperty.call(AUDIT_ACTION_TAGS, action)) {
    return AUDIT_ACTION_TAGS[action];
  }
  return { color: UNKNOWN_ACTION_COLOR, label: action || "—" };
};

// Content-health issue statuses (mirrors the health-summary count fields).
export type ContentHealthStatus =
  | "deleted"
  | "detached"
  | "broken"
  | "incomplete";

export interface ContentHealthTagStyle extends StatusTagStyle {
  // Singular per-node label shown by the ContentMap tree ("Eliminado"),
  // vs the plural count label the Dashboard shows ("Eliminados").
  mapLabel: string;
}

export const CONTENT_HEALTH_STATUS_TAGS: Record<
  ContentHealthStatus,
  ContentHealthTagStyle
> = {
  deleted: { color: "red", label: "Eliminados", mapLabel: "Eliminado" },
  detached: { color: "orange", label: "Desvinculados", mapLabel: "Desvinculado" },
  broken: { color: "volcano", label: "Rotos", mapLabel: "Referencia rota" },
  incomplete: { color: "gold", label: "Incompletos", mapLabel: "Incompleto" },
};

// Stable display order for iterating health statuses.
export const CONTENT_HEALTH_STATUS_ORDER: ContentHealthStatus[] = [
  "deleted",
  "detached",
  "broken",
  "incomplete",
];

// Platform maintenance mode → tag color/label. The Settings page and the
// Dashboard's platform status card share these ("Operativa"/"En mantenimiento").
export type PlatformStatusKey = "operational" | "maintenance";

export const PLATFORM_STATUS_TAGS: Record<PlatformStatusKey, StatusTagStyle> = {
  operational: { color: "green", label: "Operativa" },
  maintenance: { color: "red", label: "En mantenimiento" },
};
